import { useCallback, useEffect, useState } from 'react';
import { getAuth } from '@/services/auth.service';

const resolveForRole = (traderType, isAgy) => {
    if (traderType === 'IDBBroker') return { brokerType: 'IDB', isTabsVisible: false };
    if (traderType === 'Bank') {
        // Bank trader with AGY entitlement revoked is IDB-only — no market tabs.
        // Default on: drop AGY only when isAgy is explicitly false.
        if (isAgy === false) return { brokerType: 'IDB', isTabsVisible: false };
        return { brokerType: null, isTabsVisible: true };
    }
    return { brokerType: 'AGENCY', isTabsVisible: false };
};

const isValidBrokerType = (v) => v === 'AGENCY' || v === 'IDB';

export const resolveInitialBrokerType = ({
    traderType,
    isAgy,
    storageKey,
    initialOverride,
    storedValue
}) => {
    const { brokerType: locked } = resolveForRole(traderType, isAgy);
    if (locked) return locked;
    if (isValidBrokerType(initialOverride)) return initialOverride;
    if (storedValue === 'IDB') return 'IDB';
    return 'AGENCY';
};

const useIrsBrokerType = (storageKey, initialOverride, { persistOverride = true } = {}) => {
    const auth = getAuth();
    const traderType = auth?.traderType;
    const isAgy = auth?.isAgy;
    const { brokerType: locked, isTabsVisible } = resolveForRole(traderType, isAgy);

    const readDefault = () => resolveInitialBrokerType({
        traderType,
        isAgy,
        storageKey,
        initialOverride,
        storedValue:
            typeof window === 'undefined'
                ? null
                : window.localStorage?.getItem(storageKey)
    });

    const [brokerType, setBrokerTypeState] = useState(readDefault);

    useEffect(() => {
        if (locked) return;
        if (typeof window === 'undefined') return;
        if (isValidBrokerType(initialOverride)) {
            if (persistOverride) window.localStorage?.setItem(storageKey, initialOverride);
            setBrokerTypeState(initialOverride);
            return;
        }
        const stored = window.localStorage?.getItem(storageKey);
        if (isValidBrokerType(stored)) setBrokerTypeState(stored);
    }, [locked, storageKey, initialOverride, persistOverride]);

    const setBrokerType = useCallback(
        (next) => {
            if (locked) return;
            setBrokerTypeState(next);
            if (typeof window !== 'undefined') window.localStorage?.setItem(storageKey, next);
        },
        [locked, storageKey]
    );

    return { brokerType, setBrokerType, isTabsVisible };
};

export default useIrsBrokerType;
